import { createContext, useContext, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logoParDefaut from "../../assets/logo_Ramba.png";
import { useParametres } from "@/hooks/useParametres";
import { adminLogout, hasAnyRole } from "@/pages/admin/RequireAdmin";
import {
  LayoutDashboard,
  FileText,
  ShieldCheck,
  Radio,
  Languages,
  Sparkles,
  Tag,
  Lock,
  ShieldAlert,
  Users,
  BarChart3,
  Activity,
  Settings,
  GitBranch,
  LogOut,
  Mail,
  MessageSquare,
  Layers,
  ChevronDown,
  Plus,
  List,
  Menu,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Sheet, SheetContent } from "@/components/ui/sheet";

// Partagé entre AdminSidebar (dans pages/admin/Layout.tsx) et AdminTopbar (dans
// chaque page admin) : ce sont deux frères dans l'arbre, pas parent/enfant, donc
// le bouton hamburger de la topbar ne peut piloter le tiroir de la sidebar que
// via un état partagé plutôt que des props.
interface AdminSidebarContextValue {
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

const AdminSidebarContext = createContext<AdminSidebarContextValue | undefined>(undefined);

export function AdminSidebarProvider({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <AdminSidebarContext.Provider value={{ mobileOpen, setMobileOpen }}>
      {children}
    </AdminSidebarContext.Provider>
  );
}

function useAdminSidebar() {
  const ctx = useContext(AdminSidebarContext);
  if (!ctx) throw new Error("useAdminSidebar must be used within AdminSidebarProvider");
  return ctx;
}

function SidebarNav({ onNavigate }: { onNavigate: () => void }) {
  const path = useLocation().pathname;
  const navigate = useNavigate();
  const { t } = useI18n();
  const { data: parametres } = useParametres();
  const siteTitre = parametres?.["site.titre"] || "RAMBA NEWS";
  const logo = parametres?.["site.logo"] || logoParDefaut;

  function handleLogout() {
    adminLogout();
    navigate("/redaction/login", { replace: true });
  }

  // Un compte qui n'a QUE le rôle REDACTEUR n'a accès qu'à son tableau de bord
  // et à la création d'articles (qui partent automatiquement en brouillon) —
  // le reste de l'admin (paramétrage, rôles, autres contenus) reste réservé
  // à ADMIN/EDITEUR.
  const estRedacteurSeul = hasAnyRole(["REDACTEUR"]) && !hasAnyRole(["ADMIN", "EDITEUR"]);
  // Un partenaire est un partenaire FINANCIER : accès lecture seule aux
  // statistiques (individuelles/générales, par article, par contributeur —
  // via son code seulement, jamais son nom — sponsorisation, audience), sans
  // jamais pouvoir créer ni modifier un article ou une vérification.
  const estPartenaireSeul = hasAnyRole(["PARTENAIRE"]) && !hasAnyRole(["ADMIN", "EDITEUR"]);

  const NAV = estPartenaireSeul
    ? [
        { to: "/redaction/dashboard", icon: LayoutDashboard, label: t("admin.nav.dashboard") },
        { to: "/redaction/analytics", icon: BarChart3, label: t("admin.nav.analytics") },
      ]
    : estRedacteurSeul
    ? [
        { to: "/redaction/dashboard", icon: LayoutDashboard, label: t("admin.nav.dashboard") },
        {
          to: "/redaction/articles",
          icon: FileText,
          label: t("admin.nav.articles"),
          children: [
            { to: "/redaction/articles/nouveau", icon: Plus, label: t("admin.article.new.title") },
            { to: "/redaction/articles", icon: List, label: t("admin.article.viewAll") },
          ],
        },
        { to: "/redaction/factchecks", icon: ShieldCheck, label: t("admin.nav.factchecks") },
      ]
    : [
        { to: "/redaction/dashboard", icon: LayoutDashboard, label: t("admin.nav.dashboard") },
        { to: "/redaction/workflow", icon: GitBranch, label: t("admin.nav.workflow") },
        {
          to: "/redaction/articles",
          icon: FileText,
          label: t("admin.nav.articles"),
          children: [
            { to: "/redaction/articles/nouveau", icon: Plus, label: t("admin.article.new.title") },
            { to: "/redaction/articles", icon: List, label: t("admin.article.viewAll") },
            { to: "/redaction/categories", icon: Sparkles, label: t("admin.nav.categories") },
            { to: "/redaction/etiquettes", icon: Tag, label: t("admin.nav.etiquettes") },
          ],
        },
        { to: "/redaction/factchecks", icon: ShieldCheck, label: t("admin.nav.factchecks") },
        { to: "/redaction/analytics", icon: BarChart3, label: t("admin.nav.analytics") },
        { to: "/redaction/rubriques", icon: Layers, label: t("admin.nav.rubriques") },
        { to: "/redaction/live", icon: Radio, label: t("admin.live.title") },
        { to: "/redaction/newsletter", icon: Mail, label: t("admin.newsletter.title") },
        { to: "/redaction/messages", icon: MessageSquare, label: "Messages" },
        { to: "/redaction/translations", icon: Languages, label: t("admin.nav.translations") },
        { to: "/redaction/roles", icon: Users, label: t("admin.nav.roles") },
        // { to: "/redaction/disinformation", icon: Activity, label: t("admin.nav.disinformation") },
        { to: "/redaction/settings", icon: Settings, label: t("admin.nav.settings") },
      ];

  const isChildActive = (n: (typeof NAV)[number]) =>
    "children" in n && n.children.some((c) => path.startsWith(c.to));

  const [expanded, setExpanded] = useState<string | null>(
    NAV.find((n) => isChildActive(n))?.to ?? null,
  );

  return (
    <>
      <div className="px-5 py-5 border-b border-sidebar-border">
        <Link to="/redaction/dashboard" className="flex items-center gap-2" onClick={onNavigate}>
          <div className="flex h-16 w-16 items-center justify-center rounded-sm bg-sidebar-primary text-sidebar-primary-foreground font-serif font-semibold">
            {" "}
            <img
              src={logo}
              alt={`${siteTitre} Logo`}
              className="h-20 w-20 object-contain rounded-sm"
            />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">
              {siteTitre}
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-sidebar-foreground/60">
              Admin
            </div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.map((n) => {
          const Icon = n.icon;
          const hasChildren = "children" in n;
          const childActive = hasChildren && isChildActive(n);
          const active = path.startsWith(n.to);
          const isOpen = hasChildren && (expanded === n.to || childActive);

          if (!hasChildren) {
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={onNavigate}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  active
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{n.label}</span>
              </Link>
            );
          }

          return (
            <div key={n.to}>
              <button
                type="button"
                onClick={() => setExpanded((prev) => (prev === n.to ? null : n.to))}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  childActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1 text-left">{n.label}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l border-sidebar-border pl-3">
                  {n.children.map((c) => {
                    const ChildIcon = c.icon;
                    const childItemActive = path.startsWith(c.to) && (c.to !== "/redaction/articles" || path === "/redaction/articles");
                    return (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={onNavigate}
                        className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                          childItemActive
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                        }`}
                      >
                        <ChildIcon className="h-3.5 w-3.5" />
                        <span>{c.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="border-t border-sidebar-border p-3">
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <LogOut className="h-3.5 w-3.5" /> {t("admin.logout")}
        </button>
      </div>
    </>
  );
}

export function AdminSidebar() {
  const { mobileOpen, setMobileOpen } = useAdminSidebar();

  return (
    <>
      {/* Desktop : sidebar statique, toujours visible à partir de lg */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
        <SidebarNav onNavigate={() => {}} />
      </aside>

      {/* Mobile/tablette : tiroir ouvert via le bouton hamburger de AdminTopbar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="w-72 max-w-[85vw] flex flex-col gap-0 border-sidebar-border bg-sidebar p-0 text-sidebar-foreground lg:hidden"
        >
          <SidebarNav onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
}

export function AdminTopbar({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  const { t } = useI18n();
  const { setMobileOpen } = useAdminSidebar();
  return (
    <header className="border-b border-border bg-background px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label={t("admin.nav.open")}
          className="lg:hidden shrink-0 rounded-md border border-border p-2 text-foreground hover:bg-surface"
        >
          <Menu className="h-4 w-4" />
        </button>
        <div className="min-w-0">
          <h1 className="truncate font-serif text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
            {title}
          </h1>
          {subtitle && (
            <p className="truncate text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="hidden sm:inline-flex items-center gap-1.5 rounded-md bg-verified/10 px-2.5 py-1 text-xs font-semibold text-verified">
          <span className="h-1.5 w-1.5 rounded-full bg-verified" /> {t("admin.secureSession")}
        </span>
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-background text-xs font-bold">
          ED
        </div>
      </div>
    </header>
  );
}
